'use client';

/**
 * PUBLIC SITE PAGE
 *
 * Renders deployed landing pages at /site/[subdomain]
 * Uses the SHARED LandingPageRenderer component - ensuring 100% match with dashboard preview.
 */

// Required for Cloudflare Pages deployment
export const runtime = 'edge';

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';
import { projectService } from '@/services/projects';
import { crmService } from '@/services/crm';
import { analyticsService } from '@/services/analytics';
import { webhookService } from '@/services/webhook';
import { emailService } from '@/services/email';
import LandingPageRenderer, { FormConfig } from '@/features/landing-page/components/LandingPageRenderer';
import type { CompleteProject, ProductDeploy, AIResponse } from '@/features/projects/types';

export default function PublicSitePage() {
    const params = useParams();
    const subdomain = params?.subdomain as string;

    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [projectData, setProjectData] = useState<CompleteProject | null>(null);
    const [deployConfig, setDeployConfig] = useState<ProductDeploy | null>(null);

    // Time tracking
    const startTimeRef = useRef<number>(Date.now());
    const hasTrackedViewRef = useRef(false);

    useEffect(() => {
        const loadPage = async () => {
            if (!subdomain) {
                setNotFound(true);
                setLoading(false);
                return;
            }

            try {
                console.log('Looking for subdomain:', subdomain);

                const config = await projectService.getDeploymentConfigBySubdomain(subdomain);

                if (!config) {
                    console.error('No deployment config for:', subdomain);
                    setNotFound(true);
                    return;
                }

                setDeployConfig(config);

                const data = await projectService.getCompleteProject(config.project_id);

                if (!data) {
                    console.error('Project not found:', config.project_id);
                    setNotFound(true);
                    return;
                }

                setProjectData(data);

                // Track page view with analytics service
                if (!hasTrackedViewRef.current) {
                    hasTrackedViewRef.current = true;
                    analyticsService.trackPageView(config.project_id);
                    const generatedContent = data.page?.page_content as AIResponse | undefined;
                    if (generatedContent?.generationVersion === 'validation_v2') {
                        analyticsService.trackEvent(config.project_id, 'validation_page_viewed', {
                            templateKey: generatedContent.templateKey,
                            validationAction: generatedContent.validationAction,
                        });
                    }
                }

            } catch (error) {
                console.error('Failed to load site:', error);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        loadPage();

        // Track time on page when user leaves
        const handleUnload = () => {
            if (projectData?.project.id) {
                const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
                analyticsService.trackTimeOnPage(projectData.project.id, timeOnPage);
            }
        };

        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
            handleUnload();
        };
    }, [subdomain, projectData?.project.id]);

    const searchParams = useSearchParams();
    const referralCode = searchParams?.get('ref') || undefined;

    const handleEmailSubmit = async (data: { email: string; firstName?: string; lastName?: string; phone?: string; customAnswer?: string }) => {
        if (!projectData) return { success: false };
        const isValidationPage = (projectData.page?.page_content as AIResponse | undefined)?.generationVersion === 'validation_v2';
        try {
            if (isValidationPage) {
                analyticsService.trackEvent(projectData.project.id, 'validation_form_started', {
                    templateKey: (projectData.page?.page_content as AIResponse | undefined)?.templateKey,
                    validationAction: (projectData.page?.page_content as AIResponse | undefined)?.validationAction,
                });
            }

            // Generate unique referral code for the new user
            const myReferralCode = uuidv4().slice(0, 8);

            const result = await crmService.createLead(
                projectData.project.id,
                data.email,
                {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                    customAnswer: data.customAnswer,
                    referralCode: myReferralCode,
                    referredBy: referralCode
                }
            );

            if (!result.error) {
                // Track signup conversion
                analyticsService.trackSignup(projectData.project.id, data.email);
                if (isValidationPage) {
                    analyticsService.trackEvent(projectData.project.id, 'validation_form_submitted', {
                        templateKey: (projectData.page?.page_content as AIResponse | undefined)?.templateKey,
                        validationAction: (projectData.page?.page_content as AIResponse | undefined)?.validationAction,
                        hasIntentAnswer: Boolean(data.customAnswer),
                    });

                    if (data.customAnswer) {
                        analyticsService.trackEvent(projectData.project.id, 'validation_intent_question_answered', {
                            templateKey: (projectData.page?.page_content as AIResponse | undefined)?.templateKey,
                            validationAction: (projectData.page?.page_content as AIResponse | undefined)?.validationAction,
                        });
                    }
                }

                // Get dynamic position based on referrals
                const { data: positionData } = await crmService.getLeadPosition(
                    projectData.project.id,
                    myReferralCode
                );

                const position = positionData?.queue_position || 1;
                const totalWaitlist = positionData?.total_leads || 1;
                const referralsCount = positionData?.referral_count || 0;

                // Trigger webhook if enabled (async, non-blocking)
                if (deployConfig?.webhook_enabled && deployConfig?.webhook_url) {
                    const webhookPayload = webhookService.createPayload(
                        'lead.created',
                        {
                            email: data.email,
                            firstName: data.firstName,
                            lastName: data.lastName,
                            phone: data.phone,
                            customAnswer: data.customAnswer,
                            referralCode: myReferralCode,
                            referredBy: referralCode,
                        },
                        position,
                        {
                            id: projectData.project.id,
                            name: projectData.project.name,
                            subdomain: subdomain,
                            customDomain: deployConfig.custom_domain,
                        }
                    );

                    // Fire-and-forget - don't await to keep form submission fast
                    webhookService.send(
                        deployConfig.webhook_url,
                        webhookPayload,
                        deployConfig.webhook_secret
                    ).catch((err) => console.error('[Webhook] Background send failed:', err));
                }

                // Trigger emails if enabled (fire-and-forget, non-blocking)
                if (deployConfig?.email_enabled) {
                    const siteUrl = `${window.location.origin}${window.location.pathname}`;

                    // Welcome email to subscriber
                    emailService.sendWelcomeEmail(projectData.project.id, {
                        to: data.email,
                        subscriberName: data.firstName,
                        position,
                        totalWaitlist,
                        referralLink: `${siteUrl}?ref=${myReferralCode}`,
                        projectName: projectData.project.name,
                        projectUrl: siteUrl,
                    }).catch((err) => console.error('[Email] Welcome email failed:', err));

                    // Owner notification
                    emailService.sendOwnerNotification(projectData.project.id, {
                        leadEmail: data.email,
                        leadName: [data.firstName, data.lastName].filter(Boolean).join(' ') || undefined,
                        leadPhone: data.phone,
                        totalLeads: totalWaitlist,
                        projectName: projectData.project.name,
                        dashboardUrl: `${window.location.origin}/projects/${projectData.project.id}`,
                    }).catch((err) => console.error('[Email] Owner notification failed:', err));
                }

                // Return success data for the Success Card
                return {
                    success: true,
                    data: {
                        position: position,
                        totalWaitlist: totalWaitlist,
                        referralLink: `${window.location.origin}${window.location.pathname}?ref=${myReferralCode}`,
                        referralCode: myReferralCode,
                        referralsCount: referralsCount,
                        gamificationEnabled: deployConfig?.gamification_enabled ?? false,
                        milestones: deployConfig?.milestones ?? []
                    }
                };
            }
            return { success: false };
        } catch (error) {
            console.error('Submit failed:', error);
            return { success: false };
        }
    };

    const handleCtaClick = (source: 'hero' | 'finalCta') => {
        const content = projectData?.page?.page_content as AIResponse | undefined;
        if (!projectData || content?.generationVersion !== 'validation_v2') return;

        analyticsService.trackEvent(projectData.project.id, 'validation_primary_cta_clicked', {
            source,
            templateKey: content.templateKey,
            validationAction: content.validationAction,
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            </div>
        );
    }

    if (notFound || !projectData || !deployConfig) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
                <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
                <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                <a href="/" className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
                    Go Back
                </a>
            </div>
        );
    }

    const pageContent = projectData.page?.page_content as AIResponse;
    const visibleSections = projectData.page?.visible_sections || [];
    const primaryColor = projectData.page?.primary_color || '#2563EB';
    const fontFamily = projectData.page?.font_family || 'Inter';
    const fontSize = projectData.page?.font_size || 'medium';

    // Parse typography for font loading (supports both legacy and new JSON format)
    let fontsToLoad: string[] = [fontFamily];
    try {
        if (fontFamily.startsWith('{')) {
            const parsed = JSON.parse(fontFamily);
            const heading = parsed.headingFont || 'Inter';
            const body = parsed.bodyFont || 'Inter';
            fontsToLoad = heading === body ? [heading] : [heading, body];
        }
    } catch { /* use default */ }

    // Get navbar config from page_content (synced from AI preview)
    const navbarConfig = pageContent?.navbar || {
        menuItems: [
            { id: 'nav-1', label: 'Features', targetSection: 'features' },
            { id: 'nav-2', label: 'Testimonials', targetSection: 'testimonials' },
            { id: 'nav-3', label: 'FAQ', targetSection: 'faq' },
        ],
        ctaText: 'Get Started'
    };

    // Get form configuration from product_page
    const formConfig: FormConfig = {
        emailPrompt: projectData.page?.email_prompt || 'Join Our Waitlist',
        emailButtonText: projectData.page?.email_button_text || 'Join Waitlist',
        collectFirstName: projectData.page?.collect_first_name ?? true,
        collectLastName: projectData.page?.collect_last_name ?? false,
        collectPhone: projectData.page?.collect_phone ?? false,
        customQuestion: projectData.page?.custom_question || null,
        successMessage: projectData.page?.success_message || (projectData.page?.page_content as any)?.formConfig?.successMessage || 'Want to cut the line?'
    };

    if (!pageContent) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <p className="text-xl text-gray-600">Content not available</p>
            </div>
        );
    }

    return (
        <>
            {/* Google Fonts */}
            <head>
                {fontsToLoad.map((font) => (
                    <link
                        key={font}
                        href={`https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900&display=swap`}
                        rel="stylesheet"
                    />
                ))}
            </head>

            {/*
              IMPORTANT: Uses the SHARED LandingPageRenderer component
              This ensures 100% pixel-perfect match with dashboard preview.
              isEditing=false disables content editing for public visitors.
            */}
            <LandingPageRenderer
                content={pageContent}
                visibleSections={visibleSections}
                primaryColor={primaryColor}
                fontFamily={fontFamily}
                fontSize={fontSize as 'small' | 'medium' | 'large' | 'xlarge'}
                productName={projectData.project.name || 'Your Product'}
                navbarConfig={navbarConfig}
                formConfig={formConfig}
                logoUrl={projectData.page?.logo_url}
                buttonStyles={projectData.page?.button_styles}
                badgeText={projectData.page?.badge_text}
                showBadge={projectData.page?.show_badge}
                badgePosition={projectData.page?.badge_position as 'navbar' | 'hero' | undefined}
                isEditing={false}
                onEmailSubmit={handleEmailSubmit}
                onCtaClick={handleCtaClick}
            />
        </>
    );
}
